import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { EyeIcon } from "lucide-react";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SaveButton } from "@/components/ui/save-button";
import {
  FormCombobox,
  FormRecordPicker,
  useBaseForm,
} from "components/BaseForm";
import EmailHtmlFrame from "components/email/EmailHtmlFrame";
import { decodeHtml } from "helpers/emailHelper";
import { formatError } from "helpers/formatError";
import CampaignService from "services/Campaign";
import ConstantContactCampaignsService from "services/ConstantContactCampaigns";
import ContactService from "services/Contact";
import EmailFooterService from "services/EmailFooters";
import EmailTemplateService from "services/EmailTemplate";

const CONTACT_PAGE_SIZE = 100;

const schema = z.object({
  campaign_id: z.number({
    required_error: "A campaign is required",
    invalid_type_error: "A campaign is required",
  }),
  contact_id: z.number().nullable(),
});

const insertBeforeBodyEnd = (html, fragment) => {
  if (!fragment) return html;
  const at = html.indexOf("</body>");
  if (at === -1) return html + fragment;
  return html.substring(0, at) + fragment + html.substring(at);
};

const fetchContacts = campaignId => async search => {
  const response = await ContactService.getContacts({
    per_page: CONTACT_PAGE_SIZE,
    page: 1,
    sort_dir: "asc",
    sort_by: "full_name",
    campaign_id: campaignId,
    ...(search && { search }),
  });
  return response.data.map(({ id, full_name }) => ({ id, name: full_name }));
};

/**
 * Mounted only while the dialog is open, so closing it discards the chosen
 * campaign, contact, and rendered output.
 */
const PreviewBody = ({ template, footerId, providerId }) => {
  const methods = useBaseForm({
    schema,
    defaultValues: { campaign_id: null, contact_id: null },
  });
  const { control, setValue } = methods;
  const campaignId = useWatch({ control, name: "campaign_id" });

  // A contact picked for one campaign is not a member of the next.
  useEffect(() => {
    setValue("contact_id", null);
  }, [campaignId, setValue]);

  const { data: campaigns = [] } = useQuery({
    queryKey: ["campaigns-for-filters"],
    queryFn: () => CampaignService.getCampaignsForFilters(),
  });

  const campaignOptions = useMemo(
    () => campaigns.map(({ id, name }) => ({ key: id, value: id, text: name })),
    [campaigns]
  );

  const { data: footerHtml = "", isFetching: footerLoading } = useQuery({
    queryKey: ["email-footer-html", footerId],
    queryFn: async () => {
      const footer = await EmailFooterService.getEmailFooter(footerId);
      return decodeHtml(footer.html);
    },
    enabled: !!footerId,
  });

  const renderMutation = useMutation({
    mutationFn: async ({ campaign_id, contact_id }) => {
      const rendered = await EmailTemplateService.getRenderedTemplate(
        template.id,
        { campaign_id, contact_id }
      );
      const html = insertBeforeBodyEnd(rendered, footerHtml);
      if (providerId !== "constant_contact") return html;

      // Constant Contact rewrites the body on its side, so its preview is the
      // only faithful rendering for that provider.
      const preview =
        await ConstantContactCampaignsService.createPreviewCampaignActivity({
          campaign_name: campaigns.find(c => c.id === campaign_id)?.name,
          email_template_subject: template.subject,
          email_template_name: template.name,
          email_template_html: html,
        });
      if (preview.error) throw new Error(preview.error_message);
      return preview.preview_data.preview_html_content;
    },
  });

  const handleRender = methods.handleSubmit(values =>
    renderMutation.mutate(values)
  );

  return (
    <FormProvider {...methods}>
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:grid tw:grid-cols-1 tw:items-start tw:gap-4 tw:md:grid-cols-[1fr_1fr_auto]">
          <FormCombobox
            name="campaign_id"
            label="Campaign"
            required
            options={campaignOptions}
          />
          <FormRecordPicker
            name="contact_id"
            label="Contact"
            optionsKey={`email-template-preview-contacts-${campaignId}`}
            fetchOptions={fetchContacts(campaignId)}
            disabled={!campaignId}
          />
          <SaveButton
            type="button"
            className="tw:md:mt-6"
            saving={renderMutation.isPending}
            savingText="Rendering"
            disabled={footerLoading}
            onClick={handleRender}
          >
            Render
          </SaveButton>
        </div>
        {renderMutation.isError && (
          <Alert variant="destructive">
            <AlertTitle>Preview Failed</AlertTitle>
            <AlertDescription>
              {renderMutation.error.response
                ? formatError(renderMutation.error)
                : renderMutation.error.message}
            </AlertDescription>
          </Alert>
        )}
        <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-(--color-border)">
          <EmailHtmlFrame
            title="Email template preview"
            html={renderMutation.data ?? ""}
          />
        </div>
      </div>
    </FormProvider>
  );
};

/** Renders the template with merge data for a campaign and, optionally, a contact. */
const RenderEmailTemplatePreviewModal = ({
  template,
  footerId,
  providerId,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        <EyeIcon data-icon="inline-start" />
        Preview
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="tw:sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
            <DialogDescription>
              Choose a campaign, and optionally one of its contacts, to render
              the template with their merge data.
            </DialogDescription>
          </DialogHeader>
          <PreviewBody
            template={template}
            footerId={footerId}
            providerId={providerId}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

RenderEmailTemplatePreviewModal.propTypes = {
  template: PropTypes.object.isRequired,
  footerId: PropTypes.number,
  providerId: PropTypes.string.isRequired,
};

export default RenderEmailTemplatePreviewModal;
